import React from "react";
import { Eye, Stethoscope, Ear } from "lucide-react";
import { MssantePersonaPage } from "../MssantePersonaPage";

const FAQ = [
  {
    q: "Un opticien peut-il avoir une boîte MSSanté ?",
    a: "Oui. Depuis l'élargissement du référentiel MSSanté de l'Agence du Numérique en Santé (ANS), les opticiens-lunetiers diplômés disposant d'un numéro RPPS ou ADELI peuvent ouvrir une messagerie sécurisée MSSanté. C'est même fortement recommandé pour échanger avec les ophtalmologistes prescripteurs, les orthoptistes, et tous les acteurs du parcours visuel.",
  },
  {
    q: "Quelle est la différence entre @pro.mssante.fr et @opticiens.mssante.fr ?",
    a: "@pro.mssante.fr est le domaine standard partagé par tous les professionnels en boîte individuelle — il ne reflète pas votre métier. @opticiens.mssante.fr (ainsi que @optique.mssante.fr et @ohmyvue.mssante.fr) sont des sous-domaines métier exclusifs à MetaConnect, qui donnent à votre adresse une identité d'opticien immédiatement reconnaissable par les médecins.",
  },
  {
    q: "Pourquoi une boîte organisationnelle plutôt qu'individuelle pour mon cabinet d'optique ?",
    a: "Dans un cabinet ou un magasin d'optique, les échanges avec les ophtalmologistes ne doivent pas dépendre d'une seule personne. Une boîte organisationnelle (rattachée à votre SIRET, partagée par plusieurs collaborateurs) garantit la continuité des échanges même en cas de congé, de turnover ou de départ. Vos prescripteurs gardent toujours la même adresse pour vous joindre.",
  },
  {
    q: "Comment l'écosystème OhMyVue + MSSanté MetaConnect aide-t-il les opticiens ?",
    a: "OhMyVue.fr est notre plateforme dédiée au parcours visuel. Combinée à la messagerie MSSanté, elle permet d'informer automatiquement les ophtalmologistes prescripteurs lors d'un renouvellement, d'une adaptation ou d'un examen de vue. Plus de ressaisie, plus d'oublis : le devoir d'information entre opticien et médecin est respecté automatiquement.",
  },
  {
    q: "Combien coûte une messagerie MSSanté pour opticien ?",
    a: "MetaConnect propose une boîte mail MSSanté organisationnelle avec tarification transparente, sans engagement, mensuelle ou annuelle. Pour les enseignes ou groupes d'optique souhaitant un sous-domaine personnalisé (ex. paris@votre-enseigne.mssante.fr), un devis est établi sur-mesure.",
  },
  {
    q: "MetaConnect est-il interopérable avec les logiciels d'optique ?",
    a: "Oui. La messagerie MSSanté MetaConnect repose sur le standard S/MIME et l'annuaire santé national : elle est interopérable avec tous les logiciels métiers conformes (logiciels de magasin d'optique, de tiers-payant, de gestion de RDV ophtalmologiques, etc.). L'intégration avec OhMyVue.fr est native.",
  },
];

export default function MssanteOpticien() {
  return (
    <MssantePersonaPage
      title="MSSanté opticien : messagerie sécurisée · MetaConnect"
      description="Messagerie organisationnelle pour opticiens : sous-domaine @opticiens.mssante.fr, multi-utilisateurs, conforme ANS, échanges avec les ophtalmologistes."
      canonical="/mssante/opticien"
      eyebrow="MSSanté · Opticien · Magasin & cabinet d'optique"
      eyebrowIcon={Eye}
      h1="Messagerie MSSanté pour opticiens"
      intro={<>Une boîte mail organisationnelle dédiée aux <strong className="text-white">opticiens-lunetiers</strong>, avec le sous-domaine exclusif <code className="font-mono text-[var(--mc-mint)]">@opticiens.mssante.fr</code>. Échanges automatiques avec les ophtalmologistes via notre plateforme <strong className="text-white">OhMyVue.fr</strong>.</>}
      audienceLabel="Opticien"
      ctaPrimaryLabel="Créer ma boîte MSSanté opticien"
      serviceName="Messagerie MSSanté MetaConnect pour opticien"
      serviceAudience="Opticiens-lunetiers, magasins et cabinets d'optique, enseignes et chaînes d'optique"
      highlights={[
        "Sous-domaine métier @opticiens.mssante.fr — instantanément reconnaissable",
        "Multi-utilisateurs : votre équipe complète accède à la messagerie",
        "Information automatique des ophtalmologistes via OhMyVue.fr",
        "Conforme ANS · hébergement HDS · S/MIME · interopérable",
      ]}
      exchangePartners={[
        { icon: Eye, label: "Ophtalmologistes", desc: "Prescriptions de verres, comptes-rendus d'adaptation" },
        { icon: Stethoscope, label: "Orthoptistes", desc: "Bilans visuels, suivi de la rééducation" },
        { icon: Stethoscope, label: "Médecins traitants", desc: "Suivi global, dépistage de pathologies" },
      ]}
      ecosystem={{
        title: "OhMyVue.fr × MSSanté — le parcours visuel coordonné",
        description: <>En tant qu'opticien, votre quotidien c'est l'échange avec les ophtalmologistes. Combinée à <strong className="text-white">OhMyVue.fr</strong>, la messagerie MSSanté MetaConnect informe automatiquement le prescripteur lors d'un renouvellement, d'une adaptation ou d'un examen de vue. Devoir d'information respecté, temps gagné.</>,
        focusArea: "parcours visuel",
        focusIcon: Eye,
      }}
      faqs={FAQ}
      siblings={[
        { to: "/mssante/audioprothesiste", label: "MSSanté Audioprothésiste", icon: Ear, desc: "Spécifique au parcours auditif et OhMyOuie" },
        { to: "/mssante/professionnels-de-sante", label: "MSSanté Professionnels de santé", icon: Stethoscope, desc: "Médecins, paramédicaux et structures" },
        { to: "/mssante", label: "Vue d'ensemble MSSanté", icon: Eye, desc: "Tous les avantages de la boîte organisationnelle" },
      ]}
    />
  );
}
